/**
 * Portal product descriptors.
 *
 * EchoPrint and EchoEngine are two portals built from one set of libraries. A
 * descriptor is the single place where the two differ: route prefixes, cookie
 * names, Firestore collections, Cloud Storage prefixes, environment variable
 * names, and the HKDF domain that separates their session cookies.
 *
 * Two rules keep this honest, and `tests/portal/products.test.ts` enforces both:
 *
 * - Every namespaced value is derived from or checked against the product id, so
 *   a new product cannot accidentally reuse EchoPrint's collections. A shared
 *   collection would let an EchoEngine admin read EchoPrint credentials.
 * - EchoPrint's values are the ones already in production and are frozen. A
 *   rename here would orphan live documents and invalidate live cookies, so the
 *   test asserts the literal strings rather than recomputing them.
 *
 * Deliberately free of `server-only` and of any Node builtin import, because
 * `src/proxy.ts` runs in the edge runtime and needs the paths and cookie names.
 */

export const PRODUCT_IDS = ['echoprint', 'echoengine'] as const;

export type ProductId = (typeof PRODUCT_IDS)[number];

export interface ProductCollections {
  clients: string;
  loginAttempts: string;
  auditEvents: string;
  assistantUsage: string;
  contentRevisions: string;
  contentPointers: string;
  releases: string;
  releasePointers: string;
  notificationRecipients: string;
  releaseCampaigns: string;
  releaseDeliveries: string;
}

export interface PortalProduct {
  id: ProductId;
  /** Human-readable name, used in operator-facing copy and mail subjects. */
  label: string;
  homePath: string;
  loginPath: string;
  adminPath: string;
  sessionCookieName: string;
  localeCookieName: string;
  /** Header the proxy stamps with the matched pathname. Untrusted input. */
  pathnameHeader: string;
  /** Domain separation for the session cookie key. Never reuse across products. */
  sessionHkdfSalt: string;
  sessionHkdfInfo: string;
  /** Prefix for this product's environment variables, e.g. `ECHOPRINT_`. */
  envPrefix: string;
  collections: ProductCollections;
  /** Cloud Storage object prefix for release artifacts. */
  storagePrefix: string;
  /** Prefix for deterministic `mail` document ids, keeping products apart. */
  mailIdPrefix: string;
}

function collectionsFor(id: ProductId): ProductCollections {
  return {
    clients: `${id}Clients`,
    loginAttempts: `${id}LoginAttempts`,
    auditEvents: `${id}AuditEvents`,
    assistantUsage: `${id}AssistantUsage`,
    contentRevisions: `${id}ContentRevisions`,
    contentPointers: `${id}ContentPointers`,
    releases: `${id}Releases`,
    releasePointers: `${id}ReleasePointers`,
    notificationRecipients: `${id}NotificationRecipients`,
    releaseCampaigns: `${id}ReleaseCampaigns`,
    releaseDeliveries: `${id}ReleaseDeliveries`,
  };
}

export const ECHOPRINT: PortalProduct = {
  id: 'echoprint',
  label: 'EchoPrint',
  homePath: '/echoprint',
  loginPath: '/echoprint/login',
  adminPath: '/echoprint/admin',
  sessionCookieName: 'echoprint_session',
  localeCookieName: 'echoprint_locale',
  pathnameHeader: 'x-echoprint-path',
  sessionHkdfSalt: 'echoprint.session.hkdf.v1',
  sessionHkdfInfo: 'echoprint-session-cookie-aes256gcm',
  envPrefix: 'ECHOPRINT_',
  collections: collectionsFor('echoprint'),
  storagePrefix: 'echoprint/releases',
  mailIdPrefix: 'echoprint-',
};

export const ECHOENGINE: PortalProduct = {
  id: 'echoengine',
  label: 'Echo Engine',
  homePath: '/echoengine',
  loginPath: '/echoengine/login',
  adminPath: '/echoengine/admin',
  sessionCookieName: 'echoengine_session',
  localeCookieName: 'echoengine_locale',
  pathnameHeader: 'x-echoengine-path',
  sessionHkdfSalt: 'echoengine.session.hkdf.v1',
  sessionHkdfInfo: 'echoengine-session-cookie-aes256gcm',
  envPrefix: 'ECHOENGINE_',
  collections: collectionsFor('echoengine'),
  storagePrefix: 'echoengine/releases',
  mailIdPrefix: 'echoengine-',
};

export const PRODUCTS: readonly PortalProduct[] = [ECHOPRINT, ECHOENGINE];

/**
 * The product a request path belongs to, or null for the rest of the site.
 * Longest prefix wins so that a future `/echoprintx` cannot match `/echoprint`.
 */
export function productForPath(pathname: string): PortalProduct | null {
  for (const product of PRODUCTS) {
    if (pathname === product.homePath || pathname.startsWith(`${product.homePath}/`)) {
      return product;
    }
  }

  return null;
}

export function productById(id: unknown): PortalProduct | null {
  return PRODUCTS.find((product) => product.id === id) ?? null;
}
